import { ChangeEvent, ReactNode, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import StepInput from 'approot/shared/step-input/step-input';
import { LabelStyled } from 'approot/shared/styles/inputs.style';
import { calculateRequiredLessonMaterials } from './lesson-materials-calculator.utils';
import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import { useSetExpectedClassSize } from './lesson-materials.hooks';
import { useAuthorisedAction } from 'approot/shared/hooks/authorised-action.hooks';
import useUserData from 'approot/shared/user/user-data';
import { Box } from 'approot/shared/styles/box.style';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import LessonMaterialsCalculatorTable from 'approot/shared/lesson-materials/lesson-materials-calculator-table';
import { LessonMaterialCalculatorNote } from './lesson-materials-calculator.constants';
import { setSigninRedirectionPath } from 'approot/shared/signin/signin.utils';

const CLASS_SIZE_FIELD = 'classSize';
const CLASS_SIZE_MAX = 99;
const CLASS_SIZE_MIN = 1;
const CLASS_SIZE_DEFAULT = 30;

type FormData = {
  [CLASS_SIZE_FIELD]: number;
};

const LessonMaterialsCalculator = ({
  notes,
  render,
  customRow,
}: {
  notes: LessonMaterialCalculatorNote[];
  render: (content: ReactNode) => ReactNode;
  customRow?: ReactNode;
}) => {
  const { isSignedIn } = useUserData();
  const { authoriseAction, authoriseShowPaywall } = useAuthorisedAction();

  const {
    setExpectedClassSize,
    setExpectedClassSizeLoading,
    expectedClassSize,
    classId,
  } = useSetExpectedClassSize();

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [CLASS_SIZE_FIELD]: expectedClassSize || CLASS_SIZE_DEFAULT,
    },
  });

  const {
    formState: { errors },
    register,
    watch,
    setValue,
    clearErrors,
    handleSubmit,
  } = formMethods;
  const classSize = watch(CLASS_SIZE_FIELD);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (classId) {
        setExpectedClassSize({
          variables: {
            input: {
              classId,
              expectedClassSize: Number(data[CLASS_SIZE_FIELD]),
            },
          },
        }).catch(() => {});
      }
    },
    [classId, setExpectedClassSize]
  );

  const updateClassSize = useCallback(
    (value: number) => {
      setValue(CLASS_SIZE_FIELD, value);
      const isValueInRange = value <= CLASS_SIZE_MAX && value >= CLASS_SIZE_MIN;
      if (isValueInRange) {
        clearErrors(CLASS_SIZE_FIELD);
      }
    },
    [setValue, clearErrors]
  );

  const handleClassSizeOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const maxLength = 2;
      const value = e.target.value;
      if (value.length > maxLength) {
        const truncatedValue = value.substring(0, maxLength);
        updateClassSize(Number(truncatedValue));
      } else {
        updateClassSize(Number(value));
      }
    },
    [updateClassSize]
  );

  const handleIncrement = useCallback(() => {
    const newTotal: number = Number(classSize) + 1;
    updateClassSize(newTotal);
  }, [classSize, updateClassSize]);

  const handleDecrement = useCallback(() => {
    const newTotal: number = Number(classSize) - 1;
    updateClassSize(newTotal);
  }, [classSize, updateClassSize]);

  const calculatedMaterials = useMemo(
    () => calculateRequiredLessonMaterials({ notes, classSize }),
    [classSize, notes]
  );

  return calculatedMaterials.length ? (
    <>
      {render(
        <>
          <form
            onSubmit={handleSubmit(
              authoriseAction(isSignedIn, onSubmit, () => {
                setSigninRedirectionPath();
                authoriseShowPaywall();
              })
            )}
          >
            <LabelStyled htmlFor={CLASS_SIZE_FIELD} marginBottom={8}>
              How many students are in your class?
            </LabelStyled>
            <Box $alignItems="center">
              <StepInput
                id={CLASS_SIZE_FIELD}
                min={CLASS_SIZE_MIN}
                max={CLASS_SIZE_MAX}
                {...register(CLASS_SIZE_FIELD, {
                  max: {
                    value: CLASS_SIZE_MAX,
                    message: `Class size must be less than or equal to ${CLASS_SIZE_MAX}`,
                  },
                  min: {
                    value: CLASS_SIZE_MIN,
                    message: `Class size must be greater than or equal to ${CLASS_SIZE_MIN}`,
                  },
                  required: 'Please enter the number of students in your class',
                })}
                onIncrementClick={handleIncrement}
                onDecrementClick={handleDecrement}
                isDecrementDisabled={classSize <= CLASS_SIZE_MIN}
                isIncrementDisabled={classSize >= CLASS_SIZE_MAX}
                aria-required
                aria-invalid={!!errors[CLASS_SIZE_FIELD]?.message}
                aria-describedby={`${CLASS_SIZE_FIELD}-error`}
                onChange={handleClassSizeOnChange}
                $hasError={!!errors[CLASS_SIZE_FIELD]?.message}
                incrementLabel="Increase class size"
                decrementLabel="Decrease class size"
              />
              <Box $marginLeft={16}>
                <SecondaryLoadingButton
                  type="submit"
                  isDisabled={
                    !!errors[CLASS_SIZE_FIELD]?.message ||
                    setExpectedClassSizeLoading
                  }
                  isLoading={setExpectedClassSizeLoading}
                >
                  Save Class Size
                </SecondaryLoadingButton>
              </Box>
            </Box>
            {errors?.[CLASS_SIZE_FIELD] && (
              <ErrorMessageStyled id={`${CLASS_SIZE_FIELD}-error`}>
                {errors[CLASS_SIZE_FIELD]?.message}
              </ErrorMessageStyled>
            )}
          </form>
          {customRow}
          <LessonMaterialsCalculatorTable materials={calculatedMaterials} />
        </>
      )}
    </>
  ) : null;
};

export default LessonMaterialsCalculator;
